import Grid from '@mui/material/Grid';
import TextField from '../../../../../../temp/TextField';
import { Box, Button } from '@mui/material';
import Typography from '@mui/material/Typography';
import SendMatterportLinkForm from '../SendMatterportLinkForm';
import SuccessSnackbar from '../../../../../shared/feedback/SuccessSnackbar';
import FailureSnackbar from '../../../../../shared/feedback/FailureSnackbar';
import React, { useEffect, useState } from 'react';
import { useAddLinkData } from '../hooks/useAddLinkData';
import { useDeleteLinkData } from '../hooks/useRemoveLinkData';
import { Translate } from '../../../../../shared/LanguageService';
import { AuthenticatedUser } from '../../../../../shared/types';

export type MatterportAdminLabels = {
    addLinkButton: string;
    cancelLinkButton: string;
    editLinkButton: string;
    saveLinkButton: string;
    upploadSuccessText: string;
    uploadFailText: string;
    removeLinkButton: string;
    linkDeletedSuccessText: string;
    linkDeletedFailureText: string;
};

type MatterportAdminProps = {
    orderId: number;
    addedLink: string | null;
    brokerAgentEmail: string;
    user: AuthenticatedUser;
    labels: MatterportAdminLabels;
};

type ExternalProps = {
    orderId: number;
    addedLink: string | null;
    brokerAgentEmail: string;
    user: AuthenticatedUser;
};

export function MatterportAdmin({
    orderId,
    addedLink,
    labels,
    user,
    brokerAgentEmail
}: MatterportAdminProps) {
    const [link, setLink] = useState('');
    const [previousLink, setPreviousLink] = useState<string | null>('');
    const [toggleEdit, setToggleEdit] = useState(false);
    const [toggleInput, setToggleInput] = useState(true);
    const [showMatterportHasLink, setShowMatterportHasLink] = useState(false);
    const [openSuccessSnackbar, setOpenSuccessSnackbar] = useState<boolean>(false);
    const [openFailureSnackbar, setOpenFailureSnackbar] = useState<boolean>(false);
    const [linkDeletedSnackbarText, setLinkDeletedSnackbarText] = useState<boolean>(false);

    const { mutate: addMatterportLink } = useAddLinkData();

    const { mutate: deleteMatterportLink } = useDeleteLinkData();

    function handleClickAddLink() {
        if (link.length > 0) {
            setLinkDeletedSnackbarText(false);
            let matterport = { orderId, link };
            addMatterportLink(matterport, {
                onSuccess: () => {
                    setOpenSuccessSnackbar(true);
                },
                onError: () => {
                    setOpenFailureSnackbar(true);
                }
            });
        }
    }

    function handleCancel() {
        setLink('');
    }

    function handleEditLink() {
        setToggleEdit(true);
    }

    function handleUpdateLinkClick() {
        let matterport = { orderId, link };
        addMatterportLink(matterport, {
            onSuccess: () => {
                setOpenSuccessSnackbar(true);
            },
            onError: () => {
                setOpenFailureSnackbar(true);
            }
        });
        setToggleEdit(false);
    }

    function handleDeleteLink() {
        setLinkDeletedSnackbarText(true);
        deleteMatterportLink(orderId, {
            onSuccess: () => {
                setOpenSuccessSnackbar(true);
            },
            onError: () => {
                setOpenFailureSnackbar(true);
            }
        });
        setLink('');
        setToggleEdit(false);
    }

    useEffect(() => {
        if (addedLink != null) {
            setShowMatterportHasLink(true);
            setToggleInput(false);
            setPreviousLink(addedLink);
            setLink(addedLink);
        } else {
            setShowMatterportHasLink(false);
            setToggleInput(true);
            setPreviousLink('');
        }
    }, [addedLink]);

    return (
        <>
            {toggleInput ? (
                <Grid container display="flex" alignItems="center" justifyItems="center">
                    <Grid item md={8}>
                        <TextField
                            className="form-control"
                            type="text"
                            name="matterportlink"
                            value={link}
                            onChange={(e) => setLink(e.target.value)}
                        ></TextField>
                    </Grid>
                    <Grid item md={4}>
                        {link.length > 0 ? (
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                onClick={handleCancel}
                                style={{ marginLeft: '10px', height: '38px' }}
                            >
                                {labels.cancelLinkButton}
                            </Button>
                        ) : null}
                        <Button
                            type="button"
                            variant="contained"
                            color="primary"
                            className="btn btn-primary"
                            onClick={handleClickAddLink}
                            style={{ marginLeft: '10px' }}
                            name="add"
                        >
                            <i className="material-icons md-24">add</i>
                            {labels.addLinkButton}
                        </Button>
                    </Grid>
                </Grid>
            ) : null}
            {showMatterportHasLink && !toggleEdit ? (
                <Grid container>
                    <Grid item md={6} sx={{ paddingLeft: '16px' }}>
                        <Typography variant="h5">{previousLink}</Typography>
                    </Grid>
                    <Grid item md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                                type="button"
                                variant="contained"
                                sx={{ marginRight: '10px' }}
                                onClick={handleEditLink}
                            >
                                <i className="material-icons md-18">edit</i>
                                {labels.editLinkButton}
                            </Button>
                            <SendMatterportLinkForm
                                matterportUrl={link}
                                orderId={orderId}
                                brokerAgentEmail={brokerAgentEmail}
                                user={user}
                            />
                        </Box>
                    </Grid>
                </Grid>
            ) : null}
            {toggleEdit ? (
                <Grid container display="flex" alignItems="center">
                    <Grid item md={6}>
                        <TextField
                            sx={{ marginLeft: '16px', width: '90%' }}
                            className="form-control"
                            type="text"
                            name="matterportlink"
                            value={link}
                            onChange={(e) => setLink(e.target.value)}
                        ></TextField>
                    </Grid>
                    <Grid item md={6}>
                        <Button
                            type="button"
                            variant="contained"
                            color="primary"
                            onClick={handleDeleteLink}
                            style={{ marginRight: '5px', height: '34px' }}
                        >
                            <i className="material-icons">delete</i>
                            {labels.removeLinkButton}
                        </Button>
                        <Button
                            className="btn btn-primary"
                            onClick={handleUpdateLinkClick}
                            type="button"
                            variant="contained"
                            color="primary"
                        >
                            {labels.saveLinkButton}
                        </Button>
                    </Grid>
                </Grid>
            ) : null}
            <SuccessSnackbar
                open={openSuccessSnackbar}
                text={
                    !linkDeletedSnackbarText
                        ? labels.upploadSuccessText
                        : labels.linkDeletedSuccessText
                }
                onClose={() => setOpenSuccessSnackbar(false)}
            />
            <FailureSnackbar
                open={openFailureSnackbar}
                text={
                    !linkDeletedSnackbarText ? labels.uploadFailText : labels.linkDeletedFailureText
                }
                onClose={() => setOpenFailureSnackbar(false)}
            />
        </>
    );
}

export default function TranslatedMatterportAdmin(props: ExternalProps) {
    let labels: MatterportAdminLabels = {
        addLinkButton: Translate('view-order/basic-order-info/matterport/add-link-button'),
        cancelLinkButton: Translate('view-order/basic-order-info/matterport/cancel-link-button'),
        removeLinkButton: Translate('view-order/basic-order-info/matterport/remove-link-button'),
        editLinkButton: Translate('view-order/basic-order-info/matterport/edit-link-button'),
        saveLinkButton: Translate('view-order/basic-order-info/matterport/save-link-button'),
        upploadSuccessText: Translate(
            'view-order/basic-order-info/matterport/uppload-success-text'
        ),
        uploadFailText: Translate('view-order/basic-order-info/matterport/uppload-fail-text'),
        linkDeletedSuccessText: Translate(
            'view-order/basic-order-info/matterport/deleted-link-success-text'
        ),
        linkDeletedFailureText: Translate(
            'view-order/basic-order-info/matterport/deleted-link-failure-text'
        )
    };

    return <MatterportAdmin {...props} labels={labels} />;
}
